import {
  X,
  CheckCircle,
  AlertCircle,
  Loader2,
  Image as ImageIcon,
  Trash2,
  Play,
  FileText,
  Package,
  Tag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/classnames';
import type {
  QueuedImage, AnalysisMode 
} from '../../types/ai-analysis';

interface ImageQueueProps {
  readonly images: QueuedImage[];
  readonly onRemove: (id: string) => void;
  readonly onClear: () => void;
  readonly onProcessAll: () => void;
  readonly isProcessing: boolean;
  readonly currentProcessingId: string | null;
}

const MODE_ICONS: Record<AnalysisMode, typeof ImageIcon> = {
  invoice: FileText,
  'box-counting': Package,
  'product-verification': Tag,
};

const MODE_LABELS: Record<AnalysisMode, string> = {
  invoice: 'Invoice',
  'box-counting': 'Box Count',
  'product-verification': 'Product',
};

function StatusBadge({ status }: { readonly status: QueuedImage['status'] }) {
  const config = {
    pending: {
      icon: ImageIcon,
      class: 'bg-gray-100 text-gray-600',
      label: 'Pending',
    },
    analyzing: {
      icon: Loader2,
      class: 'bg-blue-100 text-blue-600',
      label: 'Analyzing',
    },
    complete: {
      icon: CheckCircle,
      class: 'bg-green-100 text-green-600',
      label: 'Complete',
    },
    error: {
      icon: AlertCircle,
      class: 'bg-red-100 text-red-600',
      label: 'Error',
    },
  }[status];

  const Icon = config.icon;

  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full', config.class)}>
      <Icon className={cn('w-3 h-3', status === 'analyzing' && 'animate-spin')} />
      {config.label}
    </span>
  );
}

function QueueItem({
  image,
  onRemove,
  isCurrentlyProcessing,
}: {
  readonly image: QueuedImage;
  readonly onRemove: () => void;
  readonly isCurrentlyProcessing: boolean;
}) {
  const ModeIcon = MODE_ICONS[image.mode];

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-2 rounded-lg border transition-colors',
        isCurrentlyProcessing && 'border-blue-300 bg-blue-50',
        image.status === 'complete' && 'border-green-200 bg-green-50',
        image.status === 'error' && 'border-red-200 bg-red-50',
        image.status === 'pending' && 'border-gray-200 bg-white'
      )}
    >
      {/* Thumbnail */}
      <div className="relative w-16 h-16 rounded overflow-hidden flex-shrink-0">
        <img
          src={`data:${image.mediaType};base64,${image.base64}`}
          alt="Queued"
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5">
          <div className="flex items-center gap-1 text-white">
            <ModeIcon className="w-3 h-3" />
            <span className="text-[10px]">{MODE_LABELS[image.mode]}</span>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <StatusBadge status={image.status} />
        </div>
        {image.error && (
          <p className="text-xs text-red-600 mt-1 truncate">{image.error}</p>
        )}
        {image.status === 'complete' && image.result && (
          <p className="text-xs text-green-700 mt-1">Analysis complete</p>
        )}
      </div>

      {/* Remove button */}
      {image.status !== 'analyzing' && (
        <button
          type="button"
          onClick={onRemove}
          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
          aria-label="Remove image"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export function ImageQueue({
  images,
  onRemove,
  onClear,
  onProcessAll,
  isProcessing,
  currentProcessingId,
}: ImageQueueProps) {
  const pendingCount = images.filter((img) => img.status === 'pending').length;
  const completeCount = images.filter((img) => img.status === 'complete').length;
  const errorCount = images.filter((img) => img.status === 'error').length;

  if (images.length === 0) {
    return null;
  }

  return (
    <div className="bg-white border rounded-lg shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-gray-500" />
          <span className="font-medium">Image Queue</span>
          <span className="text-sm text-gray-500">({images.length})</span>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <Button
              size="sm"
              onClick={onProcessAll}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-1" />
                  Process All ({pendingCount})
                </>
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            disabled={isProcessing}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Clear
          </Button>
        </div>
      </div>

      {/* Progress summary */}
      {images.length > 1 && (
        <div className="px-3 py-2 bg-gray-50 border-b flex items-center gap-4 text-sm">
          {completeCount > 0 && (
            <span className="text-green-600">
              <CheckCircle className="w-4 h-4 inline mr-1" />
              {completeCount} complete
            </span>
          )}
          {pendingCount > 0 && (
            <span className="text-gray-500">
              <ImageIcon className="w-4 h-4 inline mr-1" />
              {pendingCount} pending
            </span>
          )}
          {errorCount > 0 && (
            <span className="text-red-600">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              {errorCount} failed
            </span>
          )}
        </div>
      )}

      {/* Queue items */}
      <div className="p-2 space-y-2 max-h-64 overflow-y-auto">
        {images.map((image) => (
          <QueueItem
            key={image.id}
            image={image}
            onRemove={() => onRemove(image.id)}
            isCurrentlyProcessing={currentProcessingId === image.id}
          />
        ))}
      </div>
    </div>
  );
}
